import { useState } from 'react';
import { Badge, Button, InlineError, Input } from '../../components/ui';
import {
  dueLabel,
  isOverdue,
  followupErrorMessage,
  useLeadFollowups,
  useUpdateLeadFollowup,
} from './leadFollowups';

/**
 * A lead's open follow-ups, where the person looking at the lead can act on
 * them.
 *
 * Renders nothing at all when there are none. An empty "Follow-ups" heading on
 * seven hundred leads is furniture, and the prompt above is already the answer
 * to "there is no follow-up here".
 */
export function LeadFollowUpList({ leadId, todayIso }: { leadId: string; todayIso: string }) {
  const followups = useLeadFollowups();
  const update = useUpdateLeadFollowup();
  const [error, setError] = useState('');
  const [reschedulingId, setReschedulingId] = useState('');

  const mine = (followups.data ?? []).filter((entry) => entry.lead_id === leadId);
  if (mine.length === 0) {
    return null;
  }

  return (
    <div className="lead-followup-list">
      {error !== '' && <InlineError>{error}</InlineError>}
      <p className="tracker-field-note">Follow-ups</p>
      <ul>
        {mine.map((entry) => (
          <li key={entry.id} className="lead-followup-row">
            <input
              type="checkbox"
              checked={false}
              aria-label={`Complete ${entry.title}`}
              onChange={() => {
                setError('');
                update.mutate(
                  { taskId: entry.id, done: true },
                  { onError: (cause) => setError(followupErrorMessage(cause.message)) },
                );
              }}
            />
            <span className="lead-followup-due">
              {entry.due_date === null ? (
                'No date'
              ) : isOverdue(entry.due_date, todayIso) ? (
                <Badge tone="warning" micro>
                  {dueLabel(entry.due_date, todayIso)}
                </Badge>
              ) : (
                dueLabel(entry.due_date, todayIso)
              )}
            </span>
            {reschedulingId === entry.id ? (
              <Input
                label=""
                type="date"
                value={entry.due_date ?? ''}
                onChange={(event) => {
                  setError('');
                  update.mutate(
                    { taskId: entry.id, dueDate: event.target.value },
                    {
                      onSuccess: () => setReschedulingId(''),
                      onError: (cause) => setError(followupErrorMessage(cause.message)),
                    },
                  );
                }}
              />
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setReschedulingId(entry.id)}>
                Reschedule
              </Button>
            )}
          </li>
        ))}
      </ul>
      <p className="tracker-field-note">
        Follow-ups are ordinary tasks — they also appear under Tasks and in My Tasks, with whoever
        they are assigned to.
      </p>
    </div>
  );
}
